import React, { useState, useMemo, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Animated, useWindowDimensions } from 'react-native';
import { IActivity, KeyStage, DirectionalSequenceConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import {
    ArrowUp,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    RotateCcw,
    Undo2,
    Sparkles,
    Compass,
    HelpCircle,
    Lightbulb,
    X,
    CheckCircle2,
} from 'lucide-react-native';

interface DirectionalSequenceActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

type CommandType = 'forward' | 'backward' | 'turn_left' | 'turn_right' | 'up' | 'down' | 'left' | 'right';
type FacingDirection = 'up' | 'right' | 'down' | 'left';

interface ThemeVisuals {
    sprite: string;
    targetIcon: string;
    targetName: string;
    obstacleIcon: string;
    obstacleName: string;
    bgCellColor: string;
    gridBorderColor: string;
    themeTitle: string;
    characterName: string;
}

const THEMES: Record<string, ThemeVisuals> = {
    bee: {
        sprite: '🐝',
        targetIcon: '🌸',
        targetName: 'Flower',
        obstacleIcon: '🪨',
        obstacleName: 'Pebble',
        bgCellColor: '#FEF9C3',
        gridBorderColor: '#FDE047',
        themeTitle: 'Bee-Bot Garden Trek',
        characterName: 'Bee-Bot',
    },
    robot: {
        sprite: '🤖',
        targetIcon: '🔋',
        targetName: 'Battery',
        obstacleIcon: '🚧',
        obstacleName: 'Barrier',
        bgCellColor: '#E0F2FE',
        gridBorderColor: '#7DD3FC',
        themeTitle: 'Robot Recharge Circuit',
        characterName: 'Robot',
    },
    frog: {
        sprite: '🐸',
        targetIcon: '🪰',
        targetName: 'Dragonfly',
        obstacleIcon: '🌾',
        obstacleName: 'Reeds',
        bgCellColor: '#DCFCE7',
        gridBorderColor: '#86EFAC',
        themeTitle: 'Froggy Pond Hop',
        characterName: 'Froggy',
    },
    car: {
        sprite: '🚗',
        targetIcon: '🏁',
        targetName: 'Finish Line',
        obstacleIcon: '🛑',
        obstacleName: 'Roadworks',
        bgCellColor: '#F1F5F9',
        gridBorderColor: '#CBD5E1',
        themeTitle: 'Town Driving Journey',
        characterName: 'Car',
    },
    rocket: {
        sprite: '🚀',
        targetIcon: '🪐',
        targetName: 'Cosmic Planet',
        obstacleIcon: '☄️',
        obstacleName: 'Asteroid',
        bgCellColor: '#EDE9FE',
        gridBorderColor: '#DDD6FE',
        themeTitle: 'Space Rocket Landing',
        characterName: 'Rocket',
    },
};

export const DirectionalSequenceActivity: React.FC<DirectionalSequenceActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const { width, height } = useWindowDimensions();
    const isCompact = width < 600 || height < 750;
    const isNarrow = width < 480;
    const isShort = height < 700;

    const config = (activity.config || {}) as DirectionalSequenceConfig;

    const rows = config.gridSize?.rows ?? 4;
    const cols = config.gridSize?.cols ?? 4;
    const startPos = config.startPosition ?? { x: 0, y: 0 };
    const targetPos = config.targetPosition ?? { x: cols - 1, y: rows - 1 };
    const obstacles = config.obstacles ?? [];
    const maxCommands = config.maxCommands ?? 10;
    const themeKey = config.theme && THEMES[config.theme] ? config.theme : 'bee';
    const theme = THEMES[themeKey];
    const initialFacing: FacingDirection = config.startFacing ?? 'right';

    const gridMaxWidth = Math.min(
        width - (isNarrow ? 48 : 80),
        height * (isShort ? 0.30 : isCompact ? 0.34 : 0.40),
        290
    );

    // Determine allowed commands
    const allowedCommands: CommandType[] = useMemo(() => {
        if (config.allowedCommands && config.allowedCommands.length > 0) {
            return config.allowedCommands as CommandType[];
        }
        return ['forward', 'turn_left', 'turn_right', 'backward'];
    }, [config.allowedCommands]);

    // Types
    type ExecutionStatus = 'idle' | 'running' | 'success' | 'crashed' | 'stopped_short';

    interface PathCellRecord {
        x: number;
        y: number;
        steps: number[];
        turns: Array<{ step: number; dir: 'turn_left' | 'turn_right' }>;
        blocked?: boolean;
    }

    // State
    const [commands, setCommands] = useState<CommandType[]>([]);
    const [currentPos, setCurrentPos] = useState<{ x: number; y: number }>(startPos);
    const [facing, setFacing] = useState<FacingDirection>(initialFacing);
    const [executionStatus, setExecutionStatus] = useState<ExecutionStatus>('idle');
    const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);
    const [statusMessage, setStatusMessage] = useState<string>('Plan your moves to reach the goal!');
    const [hitObstacle, setHitObstacle] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);

    const isRunning = executionStatus === 'running';
    const hasSucceeded = executionStatus === 'success';

    // Animation values
    const spriteScaleAnim = useRef(new Animated.Value(1)).current;
    const celebrationAnim = useRef(new Animated.Value(1)).current;
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Check if a cell is an obstacle
    const isObstacleCell = (x: number, y: number): boolean => {
        return obstacles.some((obs) => obs.x === x && obs.y === y);
    };

    // Check if cell is target
    const isTargetCell = (x: number, y: number): boolean => {
        return targetPos.x === x && targetPos.y === y;
    };

    // Calculate projected path, per-cell stage events, and preview facing based on current commands
    const previewState = useMemo(() => {
        let px = startPos.x;
        let py = startPos.y;
        let pf = initialFacing;
        const cellMap = new Map<string, PathCellRecord>();

        const getOrCreate = (x: number, y: number): PathCellRecord => {
            const key = `${x},${y}`;
            let item = cellMap.get(key);
            if (!item) {
                item = { x, y, steps: [], turns: [] };
                cellMap.set(key, item);
            }
            return item;
        };

        for (let i = 0; i < commands.length; i++) {
            const cmd = commands[i];
            const stepNum = i + 1;

            if (cmd === 'turn_left' || cmd === 'turn_right') {
                pf = rotateFacing(pf, cmd === 'turn_left' ? -1 : 1);
                const cell = getOrCreate(px, py);
                cell.steps.push(stepNum);
                cell.turns.push({ step: stepNum, dir: cmd });
            } else if (cmd === 'forward') {
                const next = getNextStepInDirection(px, py, pf);
                const blocked = isOutOfBounds(next.x, next.y, cols, rows) || isObstacleCell(next.x, next.y);
                px = next.x;
                py = next.y;
                const cell = getOrCreate(px, py);
                cell.steps.push(stepNum);
                if (blocked) {
                    cell.blocked = true;
                    break;
                }
            } else if (cmd === 'backward') {
                const opp = rotateFacing(pf, 2);
                const next = getNextStepInDirection(px, py, opp);
                const blocked = isOutOfBounds(next.x, next.y, cols, rows) || isObstacleCell(next.x, next.y);
                px = next.x;
                py = next.y;
                const cell = getOrCreate(px, py);
                cell.steps.push(stepNum);
                if (blocked) {
                    cell.blocked = true;
                    break;
                }
            } else if (cmd === 'up') {
                pf = 'up';
                py -= 1;
                const cell = getOrCreate(px, py);
                cell.steps.push(stepNum);
            } else if (cmd === 'down') {
                pf = 'down';
                py += 1;
                const cell = getOrCreate(px, py);
                cell.steps.push(stepNum);
            } else if (cmd === 'left') {
                pf = 'left';
                px -= 1;
                const cell = getOrCreate(px, py);
                cell.steps.push(stepNum);
            } else if (cmd === 'right') {
                pf = 'right';
                px += 1;
                const cell = getOrCreate(px, py);
                cell.steps.push(stepNum);
            }
        }

        return {
            finalFacing: pf,
            finalPos: { x: px, y: py },
            cellMap,
            isGoalReached: isTargetCell(px, py),
        };
    }, [commands, startPos, initialFacing, cols, rows, obstacles]);

    // Destination of current programmed moves (or simulation position when active)
    const destinationPos = useMemo(() => {
        if (executionStatus !== 'idle') {
            return currentPos;
        }
        if (commands.length === 0) {
            return startPos;
        }
        return previewState.finalPos;
    }, [executionStatus, currentPos, startPos, commands.length, previewState.finalPos]);

    const destinationFacing = useMemo(() => {
        if (executionStatus !== 'idle') {
            return facing;
        }
        if (commands.length === 0) {
            return initialFacing;
        }
        return previewState.finalFacing;
    }, [executionStatus, facing, initialFacing, commands.length, previewState.finalFacing]);

    // Cell directly ahead of current destination
    const targetAhead = useMemo(() => {
        return getNextStepInDirection(destinationPos.x, destinationPos.y, destinationFacing);
    }, [destinationPos, destinationFacing]);

    const isAheadObstacle = useMemo(() => {
        return isObstacleCell(targetAhead.x, targetAhead.y);
    }, [targetAhead, obstacles]);

    const isAheadEdge = useMemo(() => {
        return isOutOfBounds(targetAhead.x, targetAhead.y, cols, rows);
    }, [targetAhead, cols, rows]);

    const isAheadBlocked = isAheadObstacle || isAheadEdge;

    // Guidance banner state (informative and encouraging, no false-positive error alerts)
    const bannerConfig = useMemo(() => {
        if (hasSucceeded) {
            return {
                style: styles.compassBannerGoal,
                textColor: styles.compassTextGoal,
                iconColor: '#15803D',
                text: `🎉 Reached the ${theme.targetName}! Fantastic work!`,
                badge: (
                    <View style={styles.goalPill}>
                        <Text style={styles.goalPillText}>⭐ Goal Reached!</Text>
                    </View>
                ),
            };
        }
        if (executionStatus === 'idle' && previewState.isGoalReached) {
            return {
                style: styles.compassBannerGoal,
                textColor: styles.compassTextGoal,
                iconColor: '#15803D',
                text: `🎉 Path reaches the ${theme.targetName}! Tap GO! to test.`,
                badge: (
                    <View style={styles.goalPill}>
                        <Text style={styles.goalPillText}>Ready to GO! 🚀</Text>
                    </View>
                ),
            };
        }
        if (executionStatus === 'running') {
            return {
                style: styles.compassBannerOk,
                textColor: styles.compassTextOk,
                iconColor: '#1D4ED8',
                text: 'Testing your program sequence...',
                badge: (
                    <View style={styles.pathOpenBadge}>
                        <Text style={styles.pathOpenBadgeText}>Moving ⬆</Text>
                    </View>
                ),
            };
        }
        if (executionStatus === 'crashed') {
            return {
                style: styles.compassBannerWarning,
                textColor: styles.compassTextWarning,
                iconColor: '#B91C1C',
                text: `${hitObstacle ? 'Hit obstacle!' : 'Went off grid!'} Tap Try Again or adjust moves.`,
                badge: (
                    <View style={styles.turnFirstBadge}>
                        <Text style={styles.turnFirstBadgeText}>Try Again 🔄</Text>
                    </View>
                ),
            };
        }
        if (executionStatus === 'stopped_short') {
            return {
                style: styles.compassBannerOk,
                textColor: styles.compassTextOk,
                iconColor: '#1D4ED8',
                text: `${theme.characterName} stopped short. Add more moves to reach the ${theme.targetName}.`,
                badge: (
                    <View style={styles.pathOpenBadge}>
                        <Text style={styles.pathOpenBadgeText}>Add Steps ✏️</Text>
                    </View>
                ),
            };
        }
        // Idle planning:
        const currentFacing = commands.length === 0 ? initialFacing : previewState.finalFacing;
        if (commands.length === 0 && isAheadBlocked) {
            return {
                style: styles.compassBannerWarning,
                textColor: styles.compassTextWarning,
                iconColor: '#B91C1C',
                prefix: `${theme.characterName} starts pointing`,
                boldText: getFacingTitle(initialFacing),
                suffix: 'Turn first!',
                badge: (
                    <View style={styles.turnFirstBadge}>
                        <Text style={styles.turnFirstBadgeText}>Turn first ↷</Text>
                    </View>
                ),
            };
        }
        return {
            style: styles.compassBannerOk,
            textColor: styles.compassTextOk,
            iconColor: '#1D4ED8',
            prefix: `${theme.characterName} is pointing`,
            boldText: getFacingTitle(currentFacing),
            badge: (
                <View style={styles.pathOpenBadge}>
                    <Text style={styles.pathOpenBadgeText}>Ready to fly ⬆</Text>
                </View>
            ),
        };
    }, [
        hasSucceeded,
        executionStatus,
        previewState.isGoalReached,
        previewState.finalFacing,
        theme.targetName,
        theme.characterName,
        hitObstacle,
        commands.length,
        initialFacing,
        isAheadBlocked,
    ]);

    // Reset from failed/crash state
    const handleResetFromFailure = () => {
        setExecutionStatus('idle');
        setHitObstacle(false);
        setCurrentPos(startPos);
        setFacing(initialFacing);
        setStatusMessage('Ready to try again! Adjust your moves or tap GO!');
    };

    // Add command
    const handleAddCommand = (cmd: CommandType) => {
        if (isRunning || hasSucceeded) return;

        // Changing programmed moves clears crash/stopped state and returns to planning
        if (executionStatus === 'crashed' || executionStatus === 'stopped_short') {
            setExecutionStatus('idle');
            setHitObstacle(false);
        }

        if (commands.length >= maxCommands) {
            setStatusMessage(`Maximum ${maxCommands} moves reached!`);
            return;
        }
        const nextCommands = [...commands, cmd];
        setCommands(nextCommands);

        // Tactile bounce on avatar when adding a move or turning
        Animated.sequence([
            Animated.timing(spriteScaleAnim, { toValue: 1.2, duration: 100, useNativeDriver: true }),
            Animated.timing(spriteScaleAnim, { toValue: 1.0, duration: 100, useNativeDriver: true }),
        ]).start();

        // Check outcome of the newly added move
        let px = startPos.x;
        let py = startPos.y;
        let pf = initialFacing;
        let collisionWarning: string | null = null;
        let reachedTarget = false;

        for (let i = 0; i < nextCommands.length; i++) {
            const c = nextCommands[i];
            if (c === 'turn_left') {
                pf = rotateFacing(pf, -1);
            } else if (c === 'turn_right') {
                pf = rotateFacing(pf, 1);
            } else if (c === 'forward') {
                const next = getNextStepInDirection(px, py, pf);
                if (isOutOfBounds(next.x, next.y, cols, rows)) {
                    collisionWarning = `⚠️ Move ${i + 1} goes off the edge of the grid! Turn first.`;
                    break;
                }
                if (isObstacleCell(next.x, next.y)) {
                    collisionWarning = `⚠️ Move ${i + 1} hits a ${theme.obstacleName.toLowerCase()}! Turn to go around it.`;
                    break;
                }
                px = next.x;
                py = next.y;
            } else if (c === 'backward') {
                const opp = rotateFacing(pf, 2);
                const next = getNextStepInDirection(px, py, opp);
                if (isOutOfBounds(next.x, next.y, cols, rows)) {
                    collisionWarning = `⚠️ Move ${i + 1} backs off the grid!`;
                    break;
                }
                if (isObstacleCell(next.x, next.y)) {
                    collisionWarning = `⚠️ Move ${i + 1} backs into a ${theme.obstacleName.toLowerCase()}!`;
                    break;
                }
                px = next.x;
                py = next.y;
            }

            if (isTargetCell(px, py)) {
                reachedTarget = true;
            }
        }

        if (collisionWarning) {
            setStatusMessage(collisionWarning);
        } else if (reachedTarget) {
            setStatusMessage(`🎉 Path reaches the ${theme.targetName}! Tap GO! to test.`);
        } else {
            setStatusMessage(`${nextCommands.length} move(s) ready. Tap GO! to test.`);
        }
    };

    // Remove last command
    const handleUndo = () => {
        if (isRunning || hasSucceeded || commands.length === 0) return;
        if (executionStatus === 'crashed' || executionStatus === 'stopped_short') {
            setExecutionStatus('idle');
            setHitObstacle(false);
        }
        setCommands((prev) => prev.slice(0, -1));
        setStatusMessage('Last move removed.');
    };

    // Clear all
    const handleClear = () => {
        if (isRunning || hasSucceeded) return;
        setCommands([]);
        setExecutionStatus('idle');
        setCurrentPos(startPos);
        setFacing(initialFacing);
        setHitObstacle(false);
        setStatusMessage('Commands cleared. Start fresh!');
    };

    // Run programmed simulation
    const handleExecuteSequence = async () => {
        if (isRunning || commands.length === 0) return;

        setExecutionStatus('running');
        setHitObstacle(false);
        setCurrentPos(startPos);
        let curX = startPos.x;
        let curY = startPos.y;
        let curFacing: FacingDirection = initialFacing;
        setFacing(curFacing);
        setStatusMessage('Executing program...');

        const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

        let arrived = false;
        let crashed = false;

        for (let i = 0; i < commands.length; i++) {
            if (!isMounted.current) return;
            const cmd = commands[i];
            setActiveStepIndex(i);

            // Sprite bounce animation
            Animated.sequence([
                Animated.timing(spriteScaleAnim, { toValue: 1.25, duration: 120, useNativeDriver: true }),
                Animated.timing(spriteScaleAnim, { toValue: 1.0, duration: 120, useNativeDriver: true }),
            ]).start();

            await delay(200);

            // Execute move
            if (cmd === 'turn_left') {
                curFacing = rotateFacing(curFacing, -1);
                setFacing(curFacing);
            } else if (cmd === 'turn_right') {
                curFacing = rotateFacing(curFacing, 1);
                setFacing(curFacing);
            } else if (cmd === 'forward') {
                const nextPos = getNextStepInDirection(curX, curY, curFacing);
                if (isOutOfBounds(nextPos.x, nextPos.y, cols, rows)) {
                    crashed = true;
                    setStatusMessage('Oops! Went off the edge of the path!');
                    break;
                }
                if (isObstacleCell(nextPos.x, nextPos.y)) {
                    crashed = true;
                    setHitObstacle(true);
                    setStatusMessage(`Bonk! Hit the ${theme.obstacleName}!`);
                    break;
                }
                curX = nextPos.x;
                curY = nextPos.y;
                setCurrentPos({ x: curX, y: curY });
            } else if (cmd === 'backward') {
                const oppFacing = rotateFacing(curFacing, 2);
                const nextPos = getNextStepInDirection(curX, curY, oppFacing);
                if (isOutOfBounds(nextPos.x, nextPos.y, cols, rows)) {
                    crashed = true;
                    setStatusMessage('Oops! Backed off the grid!');
                    break;
                }
                if (isObstacleCell(nextPos.x, nextPos.y)) {
                    crashed = true;
                    setHitObstacle(true);
                    setStatusMessage(`Bonk! Backed into the ${theme.obstacleName}!`);
                    break;
                }
                curX = nextPos.x;
                curY = nextPos.y;
                setCurrentPos({ x: curX, y: curY });
            } else if (cmd === 'up') {
                curFacing = 'up';
                setFacing(curFacing);
                const nextY = curY - 1;
                if (nextY < 0 || isObstacleCell(curX, nextY)) {
                    crashed = true;
                    break;
                }
                curY = nextY;
                setCurrentPos({ x: curX, y: curY });
            } else if (cmd === 'down') {
                curFacing = 'down';
                setFacing(curFacing);
                const nextY = curY + 1;
                if (nextY >= rows || isObstacleCell(curX, nextY)) {
                    crashed = true;
                    break;
                }
                curY = nextY;
                setCurrentPos({ x: curX, y: curY });
            } else if (cmd === 'left') {
                curFacing = 'left';
                setFacing(curFacing);
                const nextX = curX - 1;
                if (nextX < 0 || isObstacleCell(nextX, curY)) {
                    crashed = true;
                    break;
                }
                curX = nextX;
                setCurrentPos({ x: curX, y: curY });
            } else if (cmd === 'right') {
                curFacing = 'right';
                setFacing(curFacing);
                const nextX = curX + 1;
                if (nextX >= cols || isObstacleCell(nextX, curY)) {
                    crashed = true;
                    break;
                }
                curX = nextX;
                setCurrentPos({ x: curX, y: curY });
            }

            // Check if reached target
            if (isTargetCell(curX, curY)) {
                arrived = true;
                break;
            }

            await delay(300);
        }

        setActiveStepIndex(null);

        if (arrived) {
            setExecutionStatus('success');
            setCurrentPos({ x: curX, y: curY });
            setFacing(curFacing);
            setStatusMessage(`Brilliant! Reached the ${theme.targetName}! ⭐`);

            Animated.sequence([
                Animated.timing(celebrationAnim, { toValue: 1.3, duration: 250, useNativeDriver: true }),
                Animated.timing(celebrationAnim, { toValue: 1.0, duration: 250, useNativeDriver: true }),
            ]).start();

            // Record completion
            useStrokeStore.getState().setStructuredData({
                commands,
                isCorrect: true,
                stepsCount: commands.length,
                inputModeUsed: 'grid_sequence',
                answer: 'Goal Reached',
            });

            setTimeout(() => {
                if (isMounted.current) {
                    onComplete();
                }
            }, 1200);
        } else if (crashed) {
            setExecutionStatus('crashed');
            setCurrentPos({ x: curX, y: curY });
            setFacing(curFacing);
        } else {
            setExecutionStatus('stopped_short');
            setCurrentPos({ x: curX, y: curY });
            setFacing(curFacing);
            setStatusMessage(`Almost there! Add a few more steps to reach the ${theme.targetName}.`);
        }
    };

    const trafficStatus: TrafficLightStatus = useMemo(() => {
        if (hasSucceeded) return 'green';
        if (hitObstacle || executionStatus === 'crashed' || statusMessage.startsWith('⚠️')) return 'warning';
        if (commands.length > 0) return 'amber';
        return 'red';
    }, [hasSucceeded, hitObstacle, executionStatus, statusMessage, commands.length]);

    const promptText =
        config.audioPromptText ||
        config.prompt ||
        activity.instructions ||
        `Help ${theme.characterName} navigate the garden grid to reach the ${theme.targetName}!`;

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={[styles.scrollContent, isCompact && styles.scrollContentCompact]}
            showsVerticalScrollIndicator={true}
            bounces={false}
        >
            <StageCard style={[styles.stageCard, isCompact && styles.stageCardCompact]}>
                {/* Header with Prompt, Audio Button & Help Button */}
                <View style={[styles.headerRow, isNarrow && styles.headerRowNarrow]}>
                    <View style={styles.headerTextWrap}>
                        <Text style={[styles.titleText, isCompact && styles.titleTextCompact]} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                            {activity.title || theme.themeTitle}
                        </Text>
                        {!isNarrow && (
                            <Text style={styles.promptSubText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                                {promptText}
                            </Text>
                        )}
                    </View>
                    <View style={styles.headerButtonsRow}>
                        <TouchableOpacity
                            style={[styles.helpButton, isNarrow && styles.helpButtonCompact]}
                            onPress={() => setShowHelpModal(true)}
                            accessibilityRole="button"
                            accessibilityLabel="Show help and instructions"
                            testID="help-button"
                        >
                            <HelpCircle size={18} color="#4F46E5" />
                            {!isNarrow && <Text style={styles.helpButtonText}>Help</Text>}
                        </TouchableOpacity>
                        <AudioPromptButton text={promptText} compact={isNarrow} />
                    </View>
                </View>

                {isNarrow && (
                    <Text style={styles.promptSubTextNarrow} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                        {promptText}
                    </Text>
                )}

                {/* Traffic Light Status Capsule */}
                <View style={[styles.trafficLightWrap, isCompact && styles.trafficLightWrapCompact]}>
                    <TrafficLightIndicator
                        status={trafficStatus}
                        label={statusMessage}
                    />
                </View>

                {/* Direction Guidance Banner */}
                <View style={[styles.compassBanner, bannerConfig.style, isCompact && styles.compassBannerCompact]}>
                    <Compass size={16} color={bannerConfig.iconColor} />
                    <Text style={[styles.compassBannerText, bannerConfig.textColor, isCompact && styles.compassBannerTextCompact]}>
                        {bannerConfig.prefix ? `${bannerConfig.prefix} ` : ''}
                        {bannerConfig.boldText ? (
                            <Text style={styles.compassBold}>{bannerConfig.boldText}</Text>
                        ) : null}
                        {bannerConfig.suffix ? ` ${bannerConfig.suffix}` : ''}
                        {bannerConfig.text ?? ''}
                    </Text>
                    {bannerConfig.badge}
                </View>

                {/* Interactive Navigation Grid */}
                <View style={[styles.gridContainer, isCompact && styles.gridContainerCompact, { borderColor: theme.gridBorderColor, maxWidth: gridMaxWidth }]}>
                    {Array.from({ length: rows }).map((_, r) => (
                        <View key={`row-${r}`} style={styles.gridRow}>
                            {Array.from({ length: cols }).map((_, c) => {
                                const isRealAvatar = isRunning || executionStatus !== 'idle'
                                    ? (currentPos.x === c && currentPos.y === r)
                                    : (startPos.x === c && startPos.y === r);

                                const isGhostAvatar = executionStatus === 'idle' &&
                                    commands.length > 0 &&
                                    previewState.finalPos.x === c &&
                                    previewState.finalPos.y === r &&
                                    (startPos.x !== c || startPos.y !== r);

                                const realFacing = isRunning || executionStatus !== 'idle'
                                    ? facing
                                    : (previewState.finalPos.x === startPos.x && previewState.finalPos.y === startPos.y
                                        ? previewState.finalFacing
                                        : initialFacing);

                                const ghostFacing = previewState.finalFacing;

                                const isTarget = isTargetCell(c, r);
                                const isObstacle = isObstacleCell(c, r);
                                const isStart = startPos.x === c && startPos.y === r;

                                // Check if cell has steps/turns in preview path
                                const cellKey = `${c},${r}`;
                                const cellInfo = previewState.cellMap.get(cellKey);

                                // Check if cell is directly in the aim line of preview
                                const isAhead = !isRunning &&
                                    executionStatus === 'idle' &&
                                    !previewState.isGoalReached &&
                                    targetAhead.x === c &&
                                    targetAhead.y === r;

                                return (
                                    <View
                                        key={`cell-${r}-${c}`}
                                        style={[
                                            styles.gridCell,
                                            { backgroundColor: theme.bgCellColor },
                                            isStart && styles.cellStart,
                                            isTarget && styles.cellTarget,
                                            isObstacle && styles.cellObstacle,
                                            hasSucceeded && isTarget && styles.cellWon,
                                            executionStatus === 'crashed' && isRealAvatar && styles.cellCrashed,
                                        ]}
                                        accessibilityLabel={`Grid cell ${c + 1}, ${r + 1}`}
                                    >
                                        {/* Aim reticle on open square directly ahead of preview */}
                                        {isAhead && !isObstacle && !isTarget && !isRealAvatar && !isGhostAvatar && (
                                            <View style={styles.aheadAimReticle}>
                                                <Text style={styles.aheadAimText}>⬆ Next Step</Text>
                                            </View>
                                        )}

                                        {/* Goal victory badge on target when reached */}
                                        {isTarget && hasSucceeded && (
                                            <View style={styles.goalVictoryBadge}>
                                                <Text style={styles.goalVictoryBadgeText}>⭐ GOAL!</Text>
                                            </View>
                                        )}

                                        {/* Crash indicator badge on character cell when crashed */}
                                        {isRealAvatar && executionStatus === 'crashed' && (
                                            <View style={styles.crashBadge}>
                                                <Text style={styles.crashBadgeText}>
                                                    {hitObstacle ? '💥 Bonk!' : '⚠️ Oops!'}
                                                </Text>
                                            </View>
                                        )}

                                        {/* Start marker on original start position when moves are planned or running */}
                                        {isStart && (commands.length > 0 || isRunning || executionStatus !== 'idle') && (
                                            <View style={styles.startMarkerBadge}>
                                                <Text style={styles.startMarkerText}>🏁 Start</Text>
                                            </View>
                                        )}

                                        {/* Target Item */}
                                        {isTarget && (
                                            <Animated.Text
                                                style={[
                                                    styles.targetSprite,
                                                    isCompact && styles.targetSpriteCompact,
                                                    (isRealAvatar || isGhostAvatar) ? styles.targetSpriteUnderAvatar : null,
                                                    { transform: [{ scale: celebrationAnim }] },
                                                ]}
                                            >
                                                {theme.targetIcon}
                                            </Animated.Text>
                                        )}

                                        {/* Obstacle Item */}
                                        {isObstacle && !isRealAvatar && (
                                            <Text style={[styles.obstacleSprite, isCompact && styles.obstacleSpriteCompact]}>
                                                {theme.obstacleIcon}
                                            </Text>
                                        )}

                                        {/* Planned path trail breadcrumb on intermediate visited cells */}
                                        {!isRealAvatar && cellInfo && !isTarget && !isObstacle && (
                                            <View style={styles.pathBadgeContainer}>
                                                {cellInfo.turns.length > 0 ? (
                                                    <View style={styles.pathTurnBadge}>
                                                        <Text style={styles.pathTurnIconText}>
                                                            {cellInfo.turns[cellInfo.turns.length - 1].dir === 'turn_left' ? '↶' : '↷'}
                                                        </Text>
                                                        <Text style={styles.pathTurnStepText}>
                                                            {cellInfo.turns[cellInfo.turns.length - 1].step}
                                                        </Text>
                                                    </View>
                                                ) : (
                                                    <View style={styles.pathBadge}>
                                                        <Text style={styles.pathBadgeText}>
                                                            {cellInfo.steps[cellInfo.steps.length - 1]}
                                                        </Text>
                                                    </View>
                                                )}
                                            </View>
                                        )}

                                        {/* Real Character Sprite */}
                                        {isRealAvatar && (
                                            <Animated.View
                                                style={{
                                                    transform: [{ scale: spriteScaleAnim }],
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <CharacterHeadingToken
                                                    themeKey={themeKey}
                                                    facing={realFacing}
                                                    theme={theme}
                                                    isGhost={false}
                                                />
                                            </Animated.View>
                                        )}

                                        {/* Ghost / Shadow Preview Character Sprite */}
                                        {isGhostAvatar && (
                                            <View
                                                style={{
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <CharacterHeadingToken
                                                    themeKey={themeKey}
                                                    facing={ghostFacing}
                                                    theme={theme}
                                                    isGhost={true}
                                                />
                                                <View style={styles.ghostPillTag}>
                                                    <Text style={styles.ghostPillText}>👻 Shadow</Text>
                                                </View>
                                            </View>
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    ))}
                </View>

                {/* Programmed Sequence Queue Tray */}
                <View style={[styles.queueContainer, isCompact && styles.queueContainerCompact]}>
                    <View style={styles.queueHeader}>
                        <View style={styles.queueTitleRow}>
                            <Sparkles size={18} color="#F59E0B" />
                            <Text style={styles.queueTitle}>Programmed Moves:</Text>
                            <Text style={styles.queueCountBadge}>
                                {commands.length} / {maxCommands}
                            </Text>
                        </View>
                        <View style={styles.queueActions}>
                            <TouchableOpacity
                                style={styles.actionIconButton}
                                onPress={handleUndo}
                                disabled={isRunning || commands.length === 0}
                                accessibilityRole="button"
                                accessibilityLabel="Undo last move"
                            >
                                <Undo2 size={20} color={commands.length === 0 ? '#94A3B8' : '#0F172A'} />
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.actionIconButton}
                                onPress={handleClear}
                                disabled={isRunning || commands.length === 0}
                                accessibilityRole="button"
                                accessibilityLabel="Clear all moves"
                            >
                                <RotateCcw size={20} color={commands.length === 0 ? '#94A3B8' : '#DC2626'} />
                            </TouchableOpacity>
                        </View>
                    </View>

                    {/* Step pills */}
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={[styles.queueScrollList, isCompact && styles.queueScrollListCompact]}
                    >
                        {commands.length === 0 ? (
                            <Text style={styles.emptyQueueText}>
                                Tap the direction buttons below to build your path!
                            </Text>
                        ) : (
                            commands.map((cmd, idx) => {
                                const isActive = activeStepIndex === idx;
                                return (
                                    <View
                                        key={`cmd-${idx}`}
                                        style={[styles.cmdPill, isCompact && styles.cmdPillCompact, isActive && styles.cmdPillActive]}
                                    >
                                        <Text style={styles.cmdIndex}>{idx + 1}</Text>
                                        <Text style={styles.cmdIcon}>{renderCommandIcon(cmd)}</Text>
                                    </View>
                                );
                            })
                        )}
                    </ScrollView>
                </View>

                {/* Tactile 3D Command Controls */}
                <View style={[styles.controlsRow, isCompact && styles.controlsRowCompact]}>
                    {allowedCommands.map((cmd) => (
                        <TouchableOpacity
                            key={cmd}
                            style={[styles.commandButton, isCompact && styles.commandButtonCompact]}
                            onPress={() => handleAddCommand(cmd)}
                            disabled={isRunning || hasSucceeded}
                            activeOpacity={0.7}
                            accessibilityRole="button"
                            accessibilityLabel={`Add ${getCommandLabel(cmd)}`}
                        >
                            <View style={styles.commandIconWrap}>
                                {renderCommandGraphic(cmd, isCompact)}
                            </View>
                            <Text style={[styles.commandButtonLabel, isCompact && styles.commandButtonLabelCompact]}>
                                {getCommandLabel(cmd)}
                            </Text>
                            {!isCompact && (
                                <Text style={styles.commandButtonSubLabel}>
                                    {cmd === 'turn_left' || cmd === 'turn_right' ? 'Spin 90°' : cmd === 'forward' ? '1 Step' : 'Back 1'}
                                </Text>
                            )}
                        </TouchableOpacity>
                    ))}
                </View>

                {/* Hero Action "GO!" Button */}
                <View style={styles.heroButtonContainer}>
                    <JuicyButton
                        label={
                            hasSucceeded
                                ? 'GREAT JOB! ⭐'
                                : isRunning
                                ? 'MOVING...'
                                : executionStatus === 'crashed'
                                ? 'TRY AGAIN 🔄'
                                : executionStatus === 'stopped_short'
                                ? 'TRY AGAIN 🔄'
                                : 'GO!'
                        }
                        variant={
                            hasSucceeded
                                ? 'primary'
                                : executionStatus === 'crashed'
                                ? 'danger'
                                : 'primary'
                        }
                        size={isCompact ? 'medium' : 'large'}
                        disabled={
                            isRunning ||
                            (executionStatus === 'idle' && commands.length === 0) ||
                            hasSucceeded
                        }
                        onPress={
                            executionStatus === 'crashed' || executionStatus === 'stopped_short'
                                ? handleResetFromFailure
                                : handleExecuteSequence
                        }
                        testID="hero-go-button"
                    />
                </View>

                {/* Student Help & Hints Modal Overlay */}
                {showHelpModal && (
                    <View style={styles.modalOverlay} testID="help-modal">
                        <View style={styles.modalCard}>
                            {/* Modal Header */}
                            <View style={styles.modalHeaderRow}>
                                <View style={styles.modalTitleRow}>
                                    <Lightbulb size={24} color="#F59E0B" />
                                    <Text style={styles.modalTitle}>How to Guide {theme.characterName}</Text>
                                </View>
                                <TouchableOpacity
                                    style={styles.modalCloseButton}
                                    onPress={() => setShowHelpModal(false)}
                                    accessibilityRole="button"
                                    accessibilityLabel="Close help"
                                >
                                    <X size={20} color="#64748B" />
                                </TouchableOpacity>
                            </View>

                            {/* Step Cards */}
                            <ScrollView style={styles.modalScroll} showsVerticalScrollIndicator={false}>
                                <View style={styles.helpStepCard}>
                                    <View style={styles.helpStepNumber}>
                                        <Text style={styles.helpStepNumberText}>1</Text>
                                    </View>
                                    <View style={styles.helpStepContent}>
                                        <Text style={styles.helpStepTitle}>Look where the arrow points 🧭</Text>
                                        <Text style={styles.helpStepBody}>
                                            {theme.characterName} always moves in the direction its blue arrow is aiming.
                                        </Text>
                                    </View>
                                </View>

                                <View style={styles.helpStepCard}>
                                    <View style={styles.helpStepNumber}>
                                        <Text style={styles.helpStepNumberText}>2</Text>
                                    </View>
                                    <View style={styles.helpStepContent}>
                                        <Text style={styles.helpStepTitle}>Turn before you move! ↪️</Text>
                                        <Text style={styles.helpStepBody}>
                                            If a {theme.obstacleName.toLowerCase()} or the edge is blocking the way, tap <Text style={styles.helpBold}>Turn Right</Text> or <Text style={styles.helpBold}>Turn Left</Text> to point at an empty square first!
                                        </Text>
                                    </View>
                                </View>

                                <View style={styles.helpStepCard}>
                                    <View style={styles.helpStepNumber}>
                                        <Text style={styles.helpStepNumberText}>3</Text>
                                    </View>
                                    <View style={styles.helpStepContent}>
                                        <Text style={styles.helpStepTitle}>Fly Forward ⬆️</Text>
                                        <Text style={styles.helpStepBody}>
                                            Tap <Text style={styles.helpBold}>Forward</Text> to take 1 step along the path.
                                        </Text>
                                    </View>
                                </View>

                                <View style={styles.helpStepCard}>
                                    <View style={styles.helpStepNumber}>
                                        <Text style={styles.helpStepNumberText}>4</Text>
                                    </View>
                                    <View style={styles.helpStepContent}>
                                        <Text style={styles.helpStepTitle}>Press GO! 🚀</Text>
                                        <Text style={styles.helpStepBody}>
                                            When you have planned your route, tap the big green <Text style={styles.helpBold}>GO!</Text> button to test your code!
                                        </Text>
                                    </View>
                                </View>

                                {/* Level Specific Hint Card */}
                                <View style={styles.hintCard} testID="hint-box">
                                    <View style={styles.hintTitleRow}>
                                        <CheckCircle2 size={18} color="#D97706" />
                                        <Text style={styles.hintTitle}>Level Hint</Text>
                                    </View>
                                    <Text style={styles.hintBody} testID="hint-text">
                                        {config.hintText ||
                                            `Notice where ${theme.characterName} is pointing! Turn to face an open path first, then step forward to reach the ${theme.targetName.toLowerCase()}.`}
                                    </Text>
                                </View>
                            </ScrollView>

                            {/* Dismiss CTA */}
                            <View style={styles.modalCtaRow}>
                                <JuicyButton
                                    label="Got it! Let's Go 🚀"
                                    variant="primary"
                                    size="large"
                                    onPress={() => setShowHelpModal(false)}
                                    testID="close-help-button"
                                />
                            </View>
                        </View>
                    </View>
                )}
            </StageCard>
        </ScrollView>
    );
};

// Deterministic Character Heading Token (immune to emoji font variations)
const CharacterHeadingToken: React.FC<{
    themeKey: string;
    facing: FacingDirection;
    theme: ThemeVisuals;
    isGhost?: boolean;
}> = ({ themeKey, facing, theme, isGhost = false }) => {
    const rotationDeg = getHeadingDegrees(facing);

    if (isGhost) {
        return (
            <View
                style={[
                    styles.shadowAvatarBody,
                    { transform: [{ rotate: rotationDeg }] },
                ]}
            >
                <View style={styles.shadowNosePointer}>
                    <Text style={styles.shadowNosePointerText}>▲</Text>
                </View>
                <Text style={styles.shadowSpriteText}>{theme.sprite}</Text>
            </View>
        );
    }

    if (themeKey === 'bee') {
        return (
            <View
                style={[
                    styles.beeBotBody,
                    { transform: [{ rotate: rotationDeg }] },
                ]}
            >
                {/* Antennae & Front Nose Arrow Pointer */}
                <View style={styles.beeAntennaeRow}>
                    <View style={styles.beeAntennaLeft}>
                        <View style={styles.beeAntennaTip} />
                    </View>
                    <View style={styles.beeNosePointer}>
                        <Text style={styles.beeNosePointerText}>▲</Text>
                    </View>
                    <View style={styles.beeAntennaRight}>
                        <View style={styles.beeAntennaTip} />
                    </View>
                </View>

                {/* Big cartoon eyes looking forward */}
                <View style={styles.beeEyesRow}>
                    <View style={styles.beeEye}>
                        <View style={styles.beePupil} />
                    </View>
                    <View style={styles.beeEye}>
                        <View style={styles.beePupil} />
                    </View>
                </View>

                {/* Bee-Bot stripes across body */}
                <View style={styles.beeStripesContainer}>
                    <View style={styles.beeStripe} />
                    <View style={styles.beeStripe} />
                </View>
            </View>
        );
    }

    // Generic fallback for other themes
    return (
        <View
            style={[
                styles.genericTokenBody,
                {
                    borderColor: theme.gridBorderColor,
                    backgroundColor: theme.bgCellColor,
                    transform: [{ rotate: rotationDeg }],
                },
            ]}
        >
            <View style={styles.genericNosePointer}>
                <Text style={styles.genericNosePointerText}>▲</Text>
            </View>
            <Text style={styles.genericSpriteText}>{theme.sprite}</Text>
        </View>
    );
};

// Helpers
function rotateFacing(current: FacingDirection, delta: number): FacingDirection {
    const order: FacingDirection[] = ['up', 'right', 'down', 'left'];
    const idx = order.indexOf(current);
    const newIdx = (idx + delta + 4) % 4;
    return order[newIdx];
}

function getNextStepInDirection(
    x: number,
    y: number,
    direction: FacingDirection
): { x: number; y: number } {
    switch (direction) {
        case 'up':
            return { x, y: y - 1 };
        case 'right':
            return { x: x + 1, y };
        case 'down':
            return { x, y: y + 1 };
        case 'left':
            return { x: x - 1, y };
    }
}

function isOutOfBounds(x: number, y: number, cols: number, rows: number): boolean {
    return x < 0 || x >= cols || y < 0 || y >= rows;
}

function getFacingTitle(facing: FacingDirection): string {
    switch (facing) {
        case 'up':
            return 'Up ⬆️';
        case 'right':
            return 'Right ➡️';
        case 'down':
            return 'Down ⬇️';
        case 'left':
            return 'Left ⬅️';
    }
}

function getHeadingDegrees(facing: FacingDirection): string {
    switch (facing) {
        case 'up':
            return '0deg';
        case 'right':
            return '90deg';
        case 'down':
            return '180deg';
        case 'left':
            return '270deg';
    }
}

function renderCommandIcon(cmd: CommandType): string {
    switch (cmd) {
        case 'forward':
            return '⬆️';
        case 'backward':
            return '⬇️';
        case 'turn_left':
            return '↶';
        case 'turn_right':
            return '↷';
        case 'up':
            return '⬆️';
        case 'down':
            return '⬇️';
        case 'left':
            return '⬅️';
        case 'right':
            return '➡️';
    }
}

function getCommandLabel(cmd: CommandType): string {
    switch (cmd) {
        case 'forward':
            return 'Forward';
        case 'backward':
            return 'Backward';
        case 'turn_left':
            return 'Turn Left';
        case 'turn_right':
            return 'Turn Right';
        case 'up':
            return 'Up';
        case 'down':
            return 'Down';
        case 'left':
            return 'Left';
        case 'right':
            return 'Right';
    }
}

function renderCommandGraphic(cmd: CommandType, isCompact = false) {
    const iconSize = isCompact ? 22 : 28;
    switch (cmd) {
        case 'forward':
            return <ArrowUp size={iconSize} color="#0F172A" strokeWidth={3} />;
        case 'backward':
            return <ArrowDown size={iconSize} color="#0F172A" strokeWidth={3} />;
        case 'turn_left':
            return <RotateCcw size={iconSize} color="#4F46E5" strokeWidth={3} />;
        case 'turn_right':
            return (
                <RotateCcw
                    size={iconSize}
                    color="#4F46E5"
                    strokeWidth={3}
                    style={{ transform: [{ scaleX: -1 }] }}
                />
            );
        case 'up':
            return <ArrowUp size={iconSize} color="#0F172A" strokeWidth={3} />;
        case 'down':
            return <ArrowDown size={iconSize} color="#0F172A" strokeWidth={3} />;
        case 'left':
            return <ArrowLeft size={iconSize} color="#0F172A" strokeWidth={3} />;
        case 'right':
            return <ArrowRight size={iconSize} color="#0F172A" strokeWidth={3} />;
    }
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        minHeight: 0,
        width: '100%',
    },
    scrollContent: {
        padding: 10,
        paddingBottom: 24,
        alignItems: 'center',
    },
    scrollContentCompact: {
        padding: 4,
        paddingBottom: 16,
    },
    stageCard: {
        width: '100%',
        maxWidth: 620,
        paddingHorizontal: 18,
        paddingVertical: 14,
        borderRadius: 24,
        backgroundColor: '#FFFFFF',
        position: 'relative',
    },
    stageCardCompact: {
        paddingHorizontal: 8,
        paddingVertical: 10,
        borderRadius: 20,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 6,
    },
    headerRowNarrow: {
        marginBottom: 2,
    },
    headerTextWrap: {
        flex: 1,
        marginRight: 12,
    },
    headerButtonsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    helpButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#EEF2FF',
        borderRadius: 16,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderWidth: 1.5,
        borderColor: '#C7D2FE',
        gap: 4,
    },
    helpButtonCompact: {
        width: 40,
        height: 40,
        borderRadius: 20,
        paddingHorizontal: 0,
        paddingVertical: 0,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 0,
    },
    helpButtonText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#4338CA',
    },
    titleText: {
        fontSize: 20,
        fontWeight: '900',
        color: '#0F172A',
        marginBottom: 2,
    },
    titleTextCompact: {
        fontSize: 16,
        lineHeight: 20,
    },
    promptSubText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#475569',
        lineHeight: 18,
    },
    promptSubTextNarrow: {
        fontSize: 12,
        fontWeight: '600',
        color: '#475569',
        lineHeight: 16,
        marginBottom: 6,
        marginTop: 2,
    },
    trafficLightWrap: {
        marginBottom: 6,
    },
    trafficLightWrapCompact: {
        marginBottom: 4,
    },
    compassBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 5,
        borderRadius: 12,
        marginBottom: 8,
        borderWidth: 1.5,
        justifyContent: 'space-between',
    },
    compassBannerCompact: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        marginBottom: 6,
        borderRadius: 10,
    },
    compassBannerTextCompact: {
        fontSize: 11,
    },
    compassBannerOk: {
        backgroundColor: '#EFF6FF',
        borderColor: '#93C5FD',
    },
    compassBannerWarning: {
        backgroundColor: '#FEF2F2',
        borderColor: '#FCA5A5',
    },
    compassBannerGoal: {
        backgroundColor: '#F0FDF4',
        borderColor: '#86EFAC',
    },
    compassBannerText: {
        fontSize: 13,
        fontWeight: '700',
        marginLeft: 6,
        flex: 1,
    },
    compassTextOk: {
        color: '#1E40AF',
    },
    compassTextWarning: {
        color: '#991B1B',
    },
    compassTextGoal: {
        color: '#15803D',
    },
    compassBold: {
        fontWeight: '900',
    },
    turnFirstBadge: {
        backgroundColor: '#DC2626',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 8,
    },
    turnFirstBadgeText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: '900',
    },
    pathOpenBadge: {
        backgroundColor: '#16A34A',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 8,
    },
    pathOpenBadgeText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: '900',
    },
    goalPill: {
        backgroundColor: '#16A34A',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 8,
    },
    goalPillText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: '900',
    },
    gridContainer: {
        width: '100%',
        aspectRatio: 1,
        maxWidth: 290,
        alignSelf: 'center',
        borderWidth: 3,
        borderRadius: 18,
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        marginBottom: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.08,
        shadowRadius: 6,
        elevation: 3,
    },
    gridContainerCompact: {
        marginBottom: 6,
        borderRadius: 14,
        borderWidth: 2.5,
    },
    gridRow: {
        flex: 1,
        flexDirection: 'row',
    },
    gridCell: {
        flex: 1,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    cellStart: {
        borderWidth: 2,
        borderColor: '#3B82F6',
    },
    cellTarget: {
        borderWidth: 2,
        borderColor: '#EAB308',
    },
    cellObstacle: {
        backgroundColor: '#F1F5F9',
    },
    cellWon: {
        borderWidth: 3,
        borderColor: '#EAB308',
        backgroundColor: '#FEF9C3',
    },
    cellCrashed: {
        borderWidth: 3,
        borderColor: '#EF4444',
        backgroundColor: '#FEE2E2',
    },

    // Custom Bee-Bot Token Styling
    beeBotBody: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: '#FACC15',
        borderWidth: 2,
        borderColor: '#CA8A04',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 3,
        elevation: 4,
        position: 'relative',
    },
    shadowAvatarBody: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(71, 85, 105, 0.22)',
        borderWidth: 2,
        borderColor: '#64748B',
        borderStyle: 'dashed',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    shadowNosePointer: {
        position: 'absolute',
        top: -6,
        width: 14,
        height: 14,
        borderRadius: 7,
        backgroundColor: '#475569',
        alignItems: 'center',
        justifyContent: 'center',
    },
    shadowNosePointerText: {
        color: '#FFFFFF',
        fontSize: 8,
        fontWeight: '900',
    },
    shadowSpriteText: {
        fontSize: 18,
        opacity: 0.35,
    },
    ghostPillTag: {
        position: 'absolute',
        bottom: -7,
        backgroundColor: '#475569',
        borderRadius: 6,
        paddingHorizontal: 5,
        paddingVertical: 1,
        zIndex: 6,
    },
    ghostPillText: {
        color: '#FFFFFF',
        fontSize: 8,
        fontWeight: '900',
    },
    beeAntennaeRow: {
        position: 'absolute',
        top: -8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: 32,
    },
    beeAntennaLeft: {
        width: 3,
        height: 8,
        backgroundColor: '#1E293B',
        borderRadius: 2,
        alignItems: 'center',
    },
    beeAntennaRight: {
        width: 3,
        height: 8,
        backgroundColor: '#1E293B',
        borderRadius: 2,
        alignItems: 'center',
    },
    beeAntennaTip: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#EA580C',
        position: 'absolute',
        top: -4,
    },
    beeNosePointer: {
        width: 14,
        height: 14,
        borderRadius: 7,
        backgroundColor: '#2563EB',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
    },
    beeNosePointerText: {
        fontSize: 8,
        color: '#FFFFFF',
        fontWeight: '900',
        lineHeight: 10,
    },
    beeEyesRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 24,
        marginTop: 2,
        marginBottom: 2,
    },
    beeEye: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#0F172A',
        alignItems: 'center',
        justifyContent: 'center',
    },
    beePupil: {
        width: 5,
        height: 5,
        borderRadius: 2.5,
        backgroundColor: '#0F172A',
        position: 'absolute',
        top: 1,
    },
    beeStripesContainer: {
        width: 32,
        alignItems: 'center',
        gap: 3,
    },
    beeStripe: {
        width: '100%',
        height: 4,
        backgroundColor: '#1E293B',
        borderRadius: 2,
    },
    genericTokenBody: {
        width: 50,
        height: 50,
        borderRadius: 25,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2.5,
        position: 'relative',
    },
    genericNosePointer: {
        position: 'absolute',
        top: -8,
        alignSelf: 'center',
        width: 14,
        height: 14,
        borderRadius: 7,
        backgroundColor: '#2563EB',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
    },
    genericNosePointerText: {
        fontSize: 8,
        color: '#FFFFFF',
        fontWeight: '900',
        lineHeight: 10,
    },
    genericSpriteText: {
        fontSize: 26,
    },

    aheadAimReticle: {
        position: 'absolute',
        bottom: 4,
        backgroundColor: '#DCFCE7',
        borderColor: '#22C55E',
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 4,
        paddingVertical: 1,
        zIndex: 5,
    },
    aheadAimText: {
        fontSize: 9,
        fontWeight: '800',
        color: '#15803D',
    },
    aheadBlockedBadge: {
        position: 'absolute',
        top: 4,
        backgroundColor: '#FEE2E2',
        borderColor: '#EF4444',
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 4,
        paddingVertical: 1,
        zIndex: 5,
    },
    aheadBlockedText: {
        fontSize: 9,
        fontWeight: '900',
        color: '#B91C1C',
    },
    startMarkerBadge: {
        position: 'absolute',
        bottom: 2,
        backgroundColor: '#EFF6FF',
        borderColor: '#60A5FA',
        borderWidth: 1,
        borderRadius: 6,
        paddingHorizontal: 4,
        paddingVertical: 1,
        zIndex: 2,
    },
    startMarkerText: {
        fontSize: 8,
        fontWeight: '900',
        color: '#1D4ED8',
    },
    pathBadgeContainer: {
        position: 'absolute',
        top: 4,
        right: 4,
        zIndex: 3,
    },
    pathBadge: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: '#3B82F6',
        justifyContent: 'center',
        alignItems: 'center',
    },
    pathBadgeText: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '900',
    },
    pathTurnBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#4F46E5',
        borderRadius: 8,
        paddingHorizontal: 4,
        paddingVertical: 1,
        gap: 2,
    },
    pathTurnIconText: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '900',
    },
    pathTurnStepText: {
        color: '#FFFFFF',
        fontSize: 9,
        fontWeight: '900',
    },
    crashBadge: {
        position: 'absolute',
        top: -8,
        backgroundColor: '#EF4444',
        borderRadius: 8,
        paddingHorizontal: 6,
        paddingVertical: 2,
        zIndex: 10,
        elevation: 6,
    },
    crashBadgeText: {
        color: '#FFFFFF',
        fontSize: 9,
        fontWeight: '900',
    },
    goalVictoryBadge: {
        position: 'absolute',
        top: -10,
        backgroundColor: '#F59E0B',
        borderRadius: 10,
        paddingHorizontal: 6,
        paddingVertical: 2,
        zIndex: 10,
        borderWidth: 1,
        borderColor: '#FFFFFF',
        elevation: 6,
    },
    goalVictoryBadgeText: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '900',
    },
    targetSprite: {
        fontSize: 32,
    },
    targetSpriteCompact: {
        fontSize: 24,
    },
    targetSpriteUnderAvatar: {
        position: 'absolute',
        bottom: 1,
        fontSize: 18,
        opacity: 0.6,
    },
    obstacleSprite: {
        fontSize: 28,
    },
    obstacleSpriteCompact: {
        fontSize: 22,
    },
    queueContainer: {
        backgroundColor: '#F8FAFC',
        borderRadius: 16,
        padding: 10,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        marginBottom: 10,
    },
    queueContainerCompact: {
        padding: 6,
        marginBottom: 6,
        borderRadius: 12,
    },
    queueHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    queueTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    queueTitle: {
        fontSize: 14,
        fontWeight: '800',
        color: '#1E293B',
    },
    queueCountBadge: {
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
        backgroundColor: '#E2E8F0',
        paddingHorizontal: 7,
        paddingVertical: 1,
        borderRadius: 10,
    },
    queueActions: {
        flexDirection: 'row',
        gap: 6,
    },
    actionIconButton: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: '#FFFFFF',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#CBD5E1',
    },
    queueScrollList: {
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 40,
        paddingVertical: 2,
        gap: 6,
    },
    queueScrollListCompact: {
        minHeight: 32,
        gap: 4,
    },
    emptyQueueText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#94A3B8',
        fontStyle: 'italic',
        paddingVertical: 4,
    },
    cmdPill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        gap: 4,
    },
    cmdPillCompact: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 10,
    },
    cmdPillActive: {
        borderColor: '#22C55E',
        backgroundColor: '#DCFCE7',
        transform: [{ scale: 1.08 }],
    },
    cmdIndex: {
        fontSize: 11,
        fontWeight: '800',
        color: '#64748B',
    },
    cmdIcon: {
        fontSize: 16,
    },
    controlsRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 10,
    },
    controlsRowCompact: {
        gap: 6,
        marginBottom: 6,
    },
    commandButton: {
        flex: 1,
        maxWidth: 120,
        minHeight: 56,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderBottomWidth: 4,
        borderBottomColor: '#CBD5E1',
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 4,
    },
    commandButtonCompact: {
        minHeight: 46,
        paddingVertical: 2,
        borderRadius: 12,
        borderBottomWidth: 3,
    },
    commandIconWrap: {
        marginBottom: 1,
    },
    commandButtonLabel: {
        fontSize: 12,
        fontWeight: '800',
        color: '#0F172A',
    },
    commandButtonLabelCompact: {
        fontSize: 11,
    },
    commandButtonSubLabel: {
        fontSize: 9,
        fontWeight: '700',
        color: '#64748B',
        marginTop: 1,
    },
    heroButtonContainer: {
        width: '100%',
        alignItems: 'center',
    },

    // Modal Styles
    modalOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        padding: 16,
        borderRadius: 32,
    },
    modalCard: {
        width: '100%',
        maxWidth: 520,
        maxHeight: '90%',
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.25,
        shadowRadius: 20,
        elevation: 10,
    },
    modalHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottomWidth: 1.5,
        borderBottomColor: '#F1F5F9',
    },
    modalTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '900',
        color: '#0F172A',
    },
    modalCloseButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: '#F8FAFC',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    modalScroll: {
        maxHeight: 340,
        marginBottom: 16,
    },
    helpStepCard: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        backgroundColor: '#F8FAFC',
        borderRadius: 16,
        padding: 12,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 12,
    },
    helpStepNumber: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: '#4F46E5',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 2,
    },
    helpStepNumberText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '900',
    },
    helpStepContent: {
        flex: 1,
    },
    helpStepTitle: {
        fontSize: 15,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 2,
    },
    helpStepBody: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        lineHeight: 18,
    },
    helpBold: {
        fontWeight: '800',
        color: '#1E293B',
    },
    hintCard: {
        backgroundColor: '#FEF3C7',
        borderRadius: 16,
        padding: 14,
        borderWidth: 1.5,
        borderColor: '#FCD34D',
        marginTop: 4,
    },
    hintTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 4,
    },
    hintTitle: {
        fontSize: 14,
        fontWeight: '900',
        color: '#92400E',
    },
    hintBody: {
        fontSize: 13,
        fontWeight: '700',
        color: '#78350F',
        lineHeight: 18,
    },
    modalCtaRow: {
        alignItems: 'center',
        paddingTop: 8,
    },
});
